import { Link, useLocation } from 'react-router-dom'
import clsx from 'clsx'
import {
  HomeIcon,
  BellAlertIcon,
  UserGroupIcon,
  CalendarDaysIcon,
  ArrowPathIcon,
  PuzzlePieceIcon,
  UsersIcon,
  Cog6ToothIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline'
import { useUIStore } from '@/store/ui'

const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: HomeIcon },
  { name: 'Alerts', href: '/alerts', icon: BellAlertIcon },
  { name: 'Teams', href: '/teams', icon: UserGroupIcon },
  { name: 'Schedules', href: '/schedules', icon: CalendarDaysIcon },
  { name: 'Escalation Policies', href: '/escalation-policies', icon: ArrowPathIcon },
  { name: 'Integrations', href: '/integrations', icon: PuzzlePieceIcon },
  { name: 'Users', href: '/users', icon: UsersIcon },
]

const secondaryNavigation = [{ name: 'Settings', href: '/settings', icon: Cog6ToothIcon }]

export default function Sidebar() {
  const location = useLocation()
  const sidebarCollapsed = useUIStore((state) => state.sidebarCollapsed)
  const toggleSidebarCollapse = useUIStore((state) => state.toggleSidebarCollapse)

  const isActive = (href: string) => {
    if (href === '/dashboard') {
      return location.pathname === '/dashboard' || location.pathname === '/'
    }
    return location.pathname.startsWith(href)
  }

  return (
    <div
      className={clsx(
        'flex flex-col h-full bg-surface-darker border-r border-dark-700 transition-all duration-300 relative',
        sidebarCollapsed ? 'w-[72px]' : 'w-64'
      )}
    >
      {/* Logo */}
      <div className="flex items-center h-16 px-4 border-b border-dark-700">
        <Link to="/dashboard" className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-accent flex items-center justify-center flex-shrink-0">
            <BellAlertIcon className="h-5 w-5 text-dark-900" />
          </div>
          {!sidebarCollapsed && (
            <span className="text-lg font-bold text-cream">OnCall</span>
          )}
        </Link>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto no-scrollbar">
        <div className="space-y-1">
          {!sidebarCollapsed && (
            <p className="px-3 text-xs font-semibold text-cream-muted uppercase tracking-wider mb-2">
              Main Menu
            </p>
          )}
          {navigation.map((item) => (
            <Link
              key={item.name}
              to={item.href}
              className={clsx(
                'flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200',
                isActive(item.href)
                  ? 'bg-accent/10 text-accent'
                  : 'text-cream-muted hover:bg-dark-700 hover:text-cream',
                sidebarCollapsed && 'justify-center'
              )}
              title={sidebarCollapsed ? item.name : undefined}
            >
              <item.icon
                className={clsx(
                  'h-5 w-5 flex-shrink-0 transition-colors',
                  isActive(item.href) ? 'text-accent' : ''
                )}
              />
              {!sidebarCollapsed && <span>{item.name}</span>}
              {isActive(item.href) && !sidebarCollapsed && (
                <div className="ml-auto w-1.5 h-1.5 rounded-full bg-accent" />
              )}
            </Link>
          ))}
        </div>
      </nav>

      {/* Secondary Navigation */}
      <div className="px-3 py-4 border-t border-dark-700">
        {!sidebarCollapsed && (
          <p className="px-3 text-xs font-semibold text-cream-muted uppercase tracking-wider mb-2">
            System
          </p>
        )}
        {secondaryNavigation.map((item) => (
          <Link
            key={item.name}
            to={item.href}
            className={clsx(
              'flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200',
              isActive(item.href)
                ? 'bg-accent/10 text-accent'
                : 'text-cream-muted hover:bg-dark-700 hover:text-cream',
              sidebarCollapsed && 'justify-center'
            )}
            title={sidebarCollapsed ? item.name : undefined}
          >
            <item.icon
              className={clsx(
                'h-5 w-5 flex-shrink-0 transition-colors',
                isActive(item.href) ? 'text-accent' : ''
              )}
            />
            {!sidebarCollapsed && <span>{item.name}</span>}
          </Link>
        ))}
      </div>

      {/* Collapse button */}
      <button
        onClick={toggleSidebarCollapse}
        className={clsx(
          'absolute -right-3 top-20 w-6 h-6 rounded-full',
          'bg-surface border border-dark-600',
          'flex items-center justify-center',
          'text-cream-muted hover:text-cream hover:bg-dark-700',
          'transition-all duration-200',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent'
        )}
      >
        {sidebarCollapsed ? (
          <ChevronRightIcon className="h-3.5 w-3.5" />
        ) : (
          <ChevronLeftIcon className="h-3.5 w-3.5" />
        )}
      </button>
    </div>
  )
}
